/**
 * WEB-3796 (остаток 25.10) — узел всплывашки оболочки кабинета.
 *
 * Итог смены организации показывает меню профиля (`MeMenu`), а меню живёт в
 * боковом меню — на телефоне это шторка `aside.qm.float`. После перехода шторка
 * закрывается с `visibility: hidden`, свойство наследуется, и всплывашка внутри
 * неё гасла: итог смены был не виден. Поэтому узел всплывашки держит оболочка
 * (`DashboardLayout` оборачивает себя в `ShellToastHost`), а меню получает
 * только функцию показа — `useShellToast()` (решение оркестратора 25.10: узел вне
 * шторки, не обёртка с `visibility: visible` и не правка кита).
 *
 * Узел — всплывашка кита (`useToast`) в своём корне `.ds` с темой человека
 * (класс `toast-host`): стили всплывашки действуют только под `.ds`. Корень
 * ставится, только пока всплывашка видна. Без оболочки (меню отдельно в
 * тестах и витринах) показ — пустое действие, а не падение.
 */
import React, { createContext, useContext } from 'react';
import { useToast, type ToastKind } from '@/components/kit/ds';
import { useAppSelector } from '@/hooks/hooks';
import { useCabinetRootAttributes } from '@/theme/cabinetTokens';
import type { UserTheme } from '@/types';

export type ShowShellToast = (text: string, kind: ToastKind) => void;

const noop: ShowShellToast = () => undefined;

const ShellToastContext = createContext<ShowShellToast>(noop);

/** Функция показа всплывашки оболочки; без `ShellToastHost` — пустое действие. */
export function useShellToast(): ShowShellToast {
  return useContext(ShellToastContext);
}

export function ShellToastHost({ children }: { children: React.ReactNode }) {
  const theme = useAppSelector(
    (state) =>
      (state as { auth?: { user?: { theme?: UserTheme } | null } }).auth?.user
        ?.theme,
  );
  const root = useCabinetRootAttributes(theme);
  const [toast, show] = useToast();
  return (
    <ShellToastContext.Provider value={show}>
      {children}
      {toast && (
        <div {...root} className={`${root.className} toast-host`}>
          {toast}
        </div>
      )}
    </ShellToastContext.Provider>
  );
}
